/* The builder's state: the project document (with undo/redo), uploads,
   autosave + crash recovery, and every action the screens call. */

import { create } from 'zustand';
import { produce, type Draft } from 'immer';
import {
  emptyProject, normaliseLengths, newId, itemBox, sheetStats, MM_PER_IN, formatMetres, formatMoney, inkGapFor, layoutGap,
  isManualDoc, blankRollSheet, TextSpecSchema,
  type ProjectDoc, type AssetInfo, type PublicConfig, type Design, type Item, type Sheet, type BuildResult, type SheetChoice, type TextSpec
} from '@gsb/core';
import { api, uploadFile, ApiError, type SubmissionInfo, type EmailSessionInfo } from '../api';
import { nest } from './nest';
import { navigate, parse } from '../router';

export type View = 'start' | 'designs' | 'review' | 'editor' | 'mine' | 'admin' | 'ordered';
/** The workspace's left drawer (one panel at a time; null = closed, all canvas). */
export type PanelKey = 'upload' | 'designs' | 'text' | 'nest' | 'bg' | 'settings';
export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'conflict';

export interface UploadEntry {
  designId: string;
  name: string;
  progress: number;
  status: 'queued' | 'uploading' | 'processing' | 'ready' | 'failed';
  error?: string;
  localUrl?: string;
  localPx?: { w: number; h: number };
}

export interface Toast { id: number; text: string; bad?: boolean; action?: { label: string; run: () => void } }

export interface Busy { title: string; detail?: string; progress?: number | null }

interface Snapshot { doc: ProjectDoc; label: string }

export interface State {
  config: PublicConfig | null;
  projectId: string | null;
  rev: number;
  doc: ProjectDoc;
  save: { state: SaveState; at?: number; error?: string };
  assets: Record<string, AssetInfo>;
  uploads: Record<string, UploadEntry>;
  past: Snapshot[];
  future: Snapshot[];
  view: View;
  activeSheet: number;
  selection: string[];
  designSel: string[];
  busy: Busy | null;
  toasts: Toast[];
  snap: { objects: boolean; canvas: boolean; grid: boolean; gridMm: number };
  lastBuild: Pick<BuildResult, 'overall' | 'utilisation' | 'considered' | 'unplaced' | 'chosen'> | null;
  submission: SubmissionInfo | null;
  preview: boolean;
  conflict: { rev: number; doc: ProjectDoc } | null;
  recovery: { doc: ProjectDoc; at: number } | null;
  /* workspace UI (not saved with the gangsheet) */
  panel: PanelKey | null;
  sideOpen: boolean;
  reviewOpen: boolean;
  panMode: boolean;
  show: { rulers: boolean; guides: boolean; minimap: boolean };
  /** a text design to edit in the Text panel */
  textEdit: string | null;
  /** what the film looks like on screen (and in Print preview). View only: never saved, never printed. */
  canvasBg: CanvasBg;
  /** the right-hand sheet panel folded away (desktop and tablet) */
  sideHidden: boolean;
  /** Focus mode: only the canvas, the top bar and the zoom bar */
  focus: boolean;
  /** design cards showing their extra size options */
  designOpen: string[];
  /** Lock proportions: resizing keeps width and height in step (canvas handles, size fields, design sizes) */
  lockAspect: boolean;
  /** the start chooser (DTF / UV DTF, then Start new / Auto Build / Open saved) is showing */
  startOpen: boolean;
  /** the chooser was opened with New gangsheet over this (saved) gangsheet: closing it goes back to it.
      `discard`: the customer chose to start fresh without its latest, unsaved changes. */
  startFrom: { id: string; name: string; discard: boolean } | null;
  /** New gangsheet is waiting on the customer: uploads still running, or the latest changes not saved */
  freshPrompt: FreshPrompt | null;
  /* ---- Save & Email My Gangsheet (0.3e Stage B) ---- */
  /** this browser's email sign-in (null while the email features are off) */
  email: EmailSessionInfo | null;
  /** the open gangsheet: made in this browser, saved to an email (masked), part of an order */
  meta: ProjectMeta;
  /** the Save & Email / Find My Gangsheets window */
  emailFlow: { purpose: 'save' | 'find' } | null;
  /** a gangsheet that couldn't be opened here (not in this browser, or the email sign-in ended) */
  lost: 'gone' | 'session' | null;
}

export interface ProjectMeta { madeHere: boolean; savedTo: string | null; emailedAt: string | null; orderedAt: string | null; orderRef: string | null }
export const freshMeta = (): ProjectMeta => ({ madeHere: true, savedTo: null, emailedAt: null, orderedAt: null, orderRef: null });

export type FreshPrompt = { over: boolean } & (
  | { kind: 'uploading'; count: number; name: string }
  | { kind: 'save'; name: string; savedAt: number | null; error?: string });

export type CanvasBg = 'checker' | 'dark' | 'white';

/* this viewer's workspace preferences (per browser; the page works without them) */
const UI_KEY = 'gsb:ui';
interface UiPrefs { canvasBg?: CanvasBg; sideHidden?: boolean; lockAspect?: boolean; snap?: Partial<State['snap']>; guides?: boolean }
function loadUi(): UiPrefs {
  try { const v = JSON.parse(localStorage.getItem(UI_KEY) || '{}'); return v && typeof v === 'object' ? v : {}; } catch { return {}; }
}
function saveUi(p: UiPrefs) {
  try { localStorage.setItem(UI_KEY, JSON.stringify({ ...loadUi(), ...p })); } catch { /* storage blocked: the setting lasts until reload */ }
}
const uiPrefs = loadUi();

const blankDoc = () => emptyProject('dtf', 10);

export const useStore = create<State>(() => ({
  config: null,
  projectId: null,
  rev: 0,
  doc: blankDoc(),
  save: { state: 'idle' },
  assets: {},
  uploads: {},
  past: [],
  future: [],
  view: 'start',
  activeSheet: 0,
  selection: [],
  designSel: [],
  busy: null,
  toasts: [],
  snap: { objects: true, canvas: true, grid: false, gridMm: 10, ...(uiPrefs.snap || {}) },
  lastBuild: null,
  submission: null,
  preview: false,
  conflict: null,
  recovery: null,
  panel: null,
  sideOpen: false,
  reviewOpen: false,
  panMode: false,
  // the edge-margin guide is off by default: designs can't go into the margin anyway, and the guide
  // shows by itself while something is dragged near the edge
  show: { rulers: true, guides: uiPrefs.guides === true, minimap: true },
  textEdit: null,
  canvasBg: uiPrefs.canvasBg === 'dark' || uiPrefs.canvasBg === 'white' ? uiPrefs.canvasBg : 'checker',
  sideHidden: uiPrefs.sideHidden === true,
  focus: false,
  designOpen: [],
  lockAspect: uiPrefs.lockAspect !== false,
  // a fresh start (not a link to a saved gangsheet) opens with the start chooser
  startOpen: typeof location !== 'undefined' && parse(location.pathname, location.search).name === 'start',
  startFrom: null,
  freshPrompt: null,
  email: null,
  meta: freshMeta(),
  emailFlow: null,
  lost: null
}));

const set = useStore.setState;
const get = useStore.getState;

/* ------------------------------------------------------------------ toasts */
let toastSeq = 0;
export function toast(text: string, opts: { bad?: boolean; action?: Toast['action']; ms?: number } = {}) {
  const id = ++toastSeq;
  set(s => ({ toasts: [...s.toasts.slice(-2), { id, text, bad: opts.bad, action: opts.action }] }));
  setTimeout(() => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })), opts.ms || (opts.action ? 6000 : 3800));
}

/* ------------------------------------------------------------- history */
const HISTORY = 120;

/** Change the document as one undoable step. */
export function commit(label: string, recipe: (d: Draft<ProjectDoc>) => void, opts: { stale?: boolean | 'force' } = {}) {
  const s = get();
  if (s.meta.orderedAt) return;            // part of an order: read-only (Edit a copy)
  // a gangsheet laid out by hand never goes "out of date": list changes are applied to its sheet directly
  // (see isManualDoc). Changing the sheet format itself always asks for a rebuild.
  const manual = isManualDoc(s.doc);
  let next = produce(s.doc, d => {
    recipe(d);
    if (opts.stale && d.sheets.length && (opts.stale === 'force' || !manual)) d.layoutStale = true;
  });
  if (next === s.doc) return;
  if (s.config) next = normaliseLengths(next, s.config.builder, s.config.pricing as any);
  set({ doc: next, past: [...s.past.slice(-HISTORY + 1), { doc: s.doc, label }], future: [] });
  changed();
}

/** Change without an undo step (upload results, renames while typing). */
export function silently(recipe: (d: Draft<ProjectDoc>) => void) {
  const s = get();
  if (s.meta.orderedAt) return;
  let next = produce(s.doc, recipe);
  if (next === s.doc) return;
  if (s.config) next = normaliseLengths(next, s.config.builder, s.config.pricing as any);
  set({ doc: next });
  changed();
}

export function undo() {
  const s = get();
  const prev = s.past[s.past.length - 1];
  if (!prev) return;
  set({ doc: prev.doc, past: s.past.slice(0, -1), future: [{ doc: s.doc, label: prev.label }, ...s.future], selection: keepSel(prev.doc, s.selection) });
  clampSheet();
  changed();
  toast('Undone: ' + prev.label);
}
export function redo() {
  const s = get();
  const next = s.future[0];
  if (!next) return;
  set({ doc: next.doc, future: s.future.slice(1), past: [...s.past, { doc: s.doc, label: next.label }], selection: keepSel(next.doc, s.selection) });
  clampSheet();
  changed();
  toast('Redone: ' + next.label);
  // a step redone while its placing was still being worked out: place it now
  if (isManualDoc(get().doc)) void placeToMatch();
}
function keepSel(doc: ProjectDoc, sel: string[]) {
  const ids = new Set(doc.sheets.flatMap(s => s.items.map(i => i.id)));
  return sel.filter(id => ids.has(id));
}
function clampSheet() {
  const s = get();
  if (s.activeSheet >= s.doc.sheets.length) set({ activeSheet: Math.max(0, s.doc.sheets.length - 1) });
}

/* ------------------------------------------------------- autosave & recovery */
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let saving: Promise<void> | null = null;
let saveAgain = false;
/** when this gangsheet was last saved on the server (opened, or a save that went through) */
let lastSavedAt: number | null = null;

function localKey(id: string) { return 'gsb:doc:' + id; }

function changed() {
  const s = get();
  if (s.projectId) {
    try { localStorage.setItem(localKey(s.projectId), JSON.stringify({ doc: s.doc, at: Date.now(), rev: s.rev })); } catch { /* storage full or blocked */ }
  }
  set({ save: { ...s.save, state: s.projectId ? 'dirty' : 'idle' } });
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => void flushSave(), 1500);
}

/** Same content, whatever order the keys are in. */
function sameDoc(a: unknown, b: unknown): boolean {
  const norm = (v: unknown): unknown => Array.isArray(v) ? v.map(norm) : v && typeof v === 'object'
    ? Object.fromEntries(Object.keys(v as object).sort().filter(k => (v as any)[k] !== undefined).map(k => [k, norm((v as any)[k])])) : v;
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

/** The document as the server should see it (designs still uploading stay local). */
function savable(doc: ProjectDoc): ProjectDoc {
  const pending = new Set(doc.designs.filter(d => d.assetId.startsWith('pending:')).map(d => d.id));
  if (!pending.size) return doc;
  return {
    ...doc,
    designs: doc.designs.filter(d => !pending.has(d.id)),
    sheets: doc.sheets.map(s => ({ ...s, items: s.items.filter(i => !pending.has(i.designId)) }))
  };
}

export async function flushSave(): Promise<void> {
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  if (saving) { saveAgain = true; return saving; }
  const s = get();
  if (!s.projectId || s.save.state === 'conflict' || s.save.state === 'saved' || s.save.state === 'idle') return;
  const id = s.projectId, doc = s.doc;
  set({ save: { state: 'saving' } });
  saving = (async () => {
    try {
      const r = await api.save(id, get().rev, savable(doc));
      // a new gangsheet was started (or another opened) meanwhile: this answer is for the old one only
      if (get().projectId !== id) { try { localStorage.removeItem(localKey(id)); } catch { /* ignore */ } return; }
      set({ rev: r.rev });
      lastSavedAt = Date.now();
      const still = get().doc === doc;
      set({ save: { state: still ? 'saved' : 'dirty', at: Date.now() } });
      if (still) try { localStorage.removeItem(localKey(id)); } catch { /* ignore */ }
    } catch (e) {
      const err = e as ApiError;
      if (get().projectId !== id) return;      // not the gangsheet open now (its local copy stays for recovery)
      if (err.status === 409 && err.data?.doc) {
        set({ save: { state: 'conflict' }, conflict: { rev: err.data.rev, doc: err.data.doc } });
      } else if (err.status === 409 && err.data?.ordered) {
        // it became part of an order (0.3e Stage B): read-only from now on, with Edit a copy
        set(st => ({ save: { state: 'saved', at: Date.now() }, meta: { ...st.meta, orderedAt: st.meta.orderedAt || new Date().toISOString() }, view: 'ordered', reviewOpen: false }));
        toast('This gangsheet is now part of an order, so it can’t be changed. Use Edit a copy to make a new one from it.', { ms: 8000 });
      } else if ((err.status === 404 || err.status === 401) && !get().meta.madeHere) {
        // opened through an email sign-in that has ended (signed out, or out everywhere)
        set({ save: { state: 'error', error: err.message }, lost: 'session' });
      } else {
        set({ save: { state: 'error', error: err.message } });
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(() => void flushSave(), 8000);
      }
    } finally {
      saving = null;
      if (saveAgain) { saveAgain = false; if (get().save.state === 'dirty') void flushSave(); }
    }
  })();
  return saving;
}

export function resolveConflict(keep: 'mine' | 'theirs') {
  const c = get().conflict;
  if (!c) return;
  if (keep === 'theirs') {
    set({ doc: c.doc, rev: c.rev, conflict: null, past: [], future: [], save: { state: 'saved', at: Date.now() } });
    void loadAssets(c.doc.designs.map(d => d.assetId));
  } else {
    set({ rev: c.rev, conflict: null, save: { state: 'dirty' } });
    void flushSave();
  }
}

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flushSave(); });
  window.addEventListener('beforeunload', e => {
    const st = get().save.state;
    const up = Object.values(get().uploads).some(u => u.status === 'uploading' || u.status === 'queued');
    if (st === 'dirty' || st === 'saving' || up) { void flushSave(); e.preventDefault(); e.returnValue = ''; }
  });
}

/* --------------------------------------------------------------- loading */
export async function boot() {
  const config = await api.config();
  set({ config });
  set(s => ({ doc: { ...s.doc, settings: { ...s.doc.settings, gapMm: config.builder.gapMm } } }));
  await loadEmailSession();
}

export async function openProject(id: string) {
  set({ busy: { title: 'Opening your gangsheet…' } });
  try {
    const p = await api.project(id);
    const assets: Record<string, AssetInfo> = {};
    p.assets.forEach(a => { assets[a.id] = a; });
    let recovery: State['recovery'] = null;
    try {
      const raw = localStorage.getItem(localKey(id));
      if (raw) {
        const l = JSON.parse(raw);
        /* A copy here means a change the server never confirmed. Offer it when it differs from what the
           server has and was based on the server's latest version (or the one before: the save may have
           landed without its reply). Revisions, not clocks: a customer's clock can be minutes out. */
        const mine = l && l.doc ? savable(l.doc) : null;
        if (mine && typeof l.rev === 'number' && l.rev >= p.rev - 1 && sameDoc(mine, p.doc)) localStorage.removeItem(localKey(id));
        else if (mine && typeof l.rev === 'number' && l.rev >= p.rev - 1) recovery = { doc: mine, at: l.at };
        else localStorage.removeItem(localKey(id));
      }
    } catch { /* ignore */ }
    const meta: ProjectMeta = { madeHere: p.madeHere !== false, savedTo: p.savedTo || null, emailedAt: p.emailedAt || null, orderedAt: p.orderedAt || null, orderRef: p.orderRef || null };
    set({
      projectId: id, rev: p.rev, doc: withLegacyText(p.doc), assets, past: [], future: [], selection: [], designSel: [], activeSheet: 0, focus: false, designOpen: [],
      save: { state: 'saved', at: Date.parse(p.updatedAt) }, submission: p.lastSubmission, lastBuild: null, recovery: meta.orderedAt ? null : recovery, startFrom: null, freshPrompt: null,
      // an ordered gangsheet opens read-only, with Edit a copy (0.3e Stage B)
      view: meta.orderedAt ? 'ordered' : p.doc.sheets.length ? 'review' : p.doc.designs.length ? 'designs' : 'designs',
      panel: meta.orderedAt ? null : p.doc.sheets.length ? (wide() ? 'designs' : null) : p.doc.designs.length ? 'designs' : null,
      meta, lost: null, reviewOpen: false
    });
    try { localStorage.setItem('gsb:last', id); } catch { /* ignore */ }
    lastSavedAt = Date.parse(p.updatedAt) || null;
    set({ startOpen: false });
    pollAssets();
  } finally {
    set({ busy: null });
  }
}

/* Text designs made before 0.3d kept their settings only in this browser: put them in the gangsheet as it
   opens (without saving: opening a gangsheet must not change it), so they're saved with the next change. */
function withLegacyText(doc: ProjectDoc): ProjectDoc {
  const found: Array<[string, TextSpec]> = [];
  for (const d of doc.designs) {
    if (d.text) continue;
    try {
      const raw = localStorage.getItem('gsb:text:' + d.id);
      if (!raw) continue;
      const v = TextSpecSchema.safeParse({ ...LEGACY_TEXT_DEFAULTS, ...JSON.parse(raw) });
      if (v.success) found.push([d.id, v.data]);
    } catch { /* ignore */ }
  }
  if (!found.length) return doc;
  return produce(doc, d => { for (const [id, spec] of found) { const des = d.designs.find(x => x.id === id); if (des && !des.text) des.text = spec; } });
}
const LEGACY_TEXT_DEFAULTS = { text: '', font: 'montserrat-800', color: '#0B1220', align: 'center', letterSpacing: 0, lineHeight: 1.1, outline: false, outlineColor: '#FFFFFF', outlineWidth: 4, widthMm: 250 };

export function restoreRecovery(yes: boolean) {
  const r = get().recovery;
  if (!r) return;
  if (yes) {
    set({ doc: r.doc, recovery: null, past: [{ doc: get().doc, label: 'Restore' }] });
    changed();
    void loadAssets(r.doc.designs.map(d => d.assetId).filter(a => !a.startsWith('pending:')));
    toast('Restored your unsaved changes.');
  } else {
    set({ recovery: null });
    try { localStorage.removeItem(localKey(get().projectId || '')); } catch { /* ignore */ }
  }
}

async function ensureProject(): Promise<string> {
  const s = get();
  if (s.projectId) return s.projectId;
  const r = await api.createProject(s.doc.designs.length ? savable(s.doc) : undefined);
  set({ projectId: r.id, rev: r.rev, meta: { ...freshMeta(), savedTo: r.savedTo || null } });
  // signed in by email: a new gangsheet is saved to that email straight away (0.3e Stage B). Say so the
  // first time on this device, with the way out; the saved status shows it from then on.
  if (r.savedTo) {
    let noted = false;
    try { noted = !!localStorage.getItem('gsb:autosave-noted'); localStorage.setItem('gsb:autosave-noted', '1'); } catch { /* ignore */ }
    if (!noted) {
      const id = r.id;
      toast('New gangsheets are saved to ' + r.savedTo + ' while you’re signed in.', { action: { label: 'Don’t save this one', run: () => void removeFromEmail(id) }, ms: 9000 });
    }
  }
  lastSavedAt = Date.now();
  try { localStorage.setItem('gsb:last', r.id); } catch { /* ignore */ }
  navigate('/p/' + r.id, true);
  return r.id;
}

export function newProject(transfer: 'dtf' | 'uv' = get().doc.transfer) {
  const cfg = get().config;
  set({
    projectId: null, rev: 0, doc: emptyProject(transfer, cfg?.builder.gapMm ?? 10), assets: {}, uploads: {}, past: [], future: [],
    selection: [], designSel: [], activeSheet: 0, save: { state: 'idle' }, submission: null, lastBuild: null, view: 'start', panel: null, reviewOpen: false, focus: false, designOpen: [],
    startOpen: true, startFrom: null, freshPrompt: null, meta: freshMeta()
  });
  lastSavedAt = null;
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  navigate('/');
}

/* ------------------------------------------------------- New gangsheet (0.3e)
   One way to start fresh, from the rail button, the phone +, the ⋯ menu and My gang sheets. The open
   gangsheet is never deleted or overwritten: its latest changes are saved first (and if that fails the
   customer chooses: try again, keep editing, or start fresh without them). Then the start chooser opens
   over it, with the way back; nothing new is stored on the server until the new gangsheet's first artwork
   is added. */
const busyUploads = () => Object.values(get().uploads).filter(u => u.status === 'queued' || u.status === 'uploading' || u.status === 'processing').length;

/** Save now and wait, including a save already on its way and changes made while it ran. */
async function saveAndWait(): Promise<void> {
  for (let i = 0; i < 4; i++) {
    const st = get().save.state;
    if (!get().projectId || st === 'saved' || st === 'idle' || st === 'conflict') return;
    if (i > 0 && st === 'error') return;          // tried, and it failed
    await flushSave();
  }
}

/**
 * New gangsheet. `over` (the workspace): the chooser opens over the open gangsheet, with the way back.
 * From My gang sheets (`over: false`) it simply starts a new one once the open one is safe.
 */
let freshRun = 0;   // the latest New gangsheet request: a newer one, or Keep editing, cancels older ones
const pause = (ms: number) => new Promise(r => setTimeout(r, ms));

export async function startFresh(opts: { over?: boolean; waitForUploads?: boolean; discard?: boolean } = {}): Promise<void> {
  const over = opts.over !== false;
  const run = ++freshRun;
  const live = () => run === freshRun;
  const prompt = (p: FreshPrompt) => { if (live()) set({ freshPrompt: p, busy: null }); };
  // a slow save gets the wait screen (a quick one just goes through), so nothing else can start meanwhile
  let waitShown = false;
  const waitTimer = setTimeout(() => { if (live()) { waitShown = true; set({ busy: { title: 'Saving your gangsheet…' } }); } }, 300);
  const done = () => { clearTimeout(waitTimer); if (waitShown || get().busy?.title === 'Saving your gangsheet…' || get().busy?.title === 'Finishing your uploads…') set({ busy: null }); };
  try {
    for (let pass = 0; pass < 4; pass++) {
      /* 1. files still uploading go into this gangsheet first */
      if (busyUploads()) {
        if (!opts.waitForUploads) { prompt({ over, kind: 'uploading', count: busyUploads(), name: get().doc.name }); return; }
        set({ freshPrompt: null });
        const was = get().projectId, until = Date.now() + 3 * 60_000;
        while (busyUploads() && Date.now() < until) {
          const n = busyUploads();
          set({ busy: { title: 'Finishing your uploads…', detail: (n === 1 ? '1 file' : n + ' files') + ' still uploading. Then you can start your new gangsheet.' } });
          await pause(400);
          // cancelled, or the customer is somewhere else now
          if (!live() || (was && get().projectId && get().projectId !== was)) return;
        }
        set({ busy: null });
        if (busyUploads()) { prompt({ over, kind: 'uploading', count: busyUploads(), name: get().doc.name }); return; }
      }
      /* 2. copies still being placed on a sheet laid out by hand */
      await placementsSettled();
      if (!live()) return;
      /* 3. artwork with no server copy yet (its first save failed): make one, or ask */
      let s = get();
      if (!s.projectId) {
        if (!s.doc.designs.some(d => !d.assetId.startsWith('pending:'))) { if (!busyUploads()) { newProject(); return; } continue; }
        if (!opts.discard) {
          try { await ensureProject(); } catch { prompt({ over, kind: 'save', name: s.doc.name, savedAt: null }); return; }
          if (!live()) return;
        } else { newProject(); return; }
      }
      /* 4. save the latest changes and wait for it */
      if (!opts.discard) {
        await saveAndWait();
        if (!live()) return;
        const st = get().save;
        if (st.state === 'conflict') { set({ freshPrompt: null }); toast('This gangsheet was changed in another tab. Choose which version to keep first.', { bad: true }); return; }
        if (st.state !== 'saved' && st.state !== 'idle') { prompt({ over, kind: 'save', name: get().doc.name, savedAt: lastSavedAt, error: st.error }); return; }
      }
      // anything that started meanwhile (a file added during the save): round again
      if (busyUploads()) continue;
      s = get();
      set({ freshPrompt: null });
      if (!over) {
        if (opts.discard) {
          if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
          try { localStorage.removeItem(localKey(s.projectId!)); } catch { /* ignore */ }
        }
        newProject();
        return;
      }
      set({
        startOpen: true, startFrom: { id: s.projectId!, name: s.doc.name, discard: !!opts.discard },
        selection: [], reviewOpen: false, focus: false, sideOpen: false
      });
      return;
    }
    prompt({ over, kind: 'uploading', count: busyUploads(), name: get().doc.name });
  } finally {
    done();
  }
}
/** Keep editing: forget New gangsheet (also one still waiting for uploads or a save). */
export function cancelStartFresh() { freshRun++; set({ freshPrompt: null }); }

/* ------------------------------------------------------------- starting
   The start chooser: DTF or UV DTF, then how to begin. Nothing is saved on the server until the first
   artwork is added. */
const WELCOMED = 'gsb:welcomed';
export function firstVisit(): boolean { try { return !localStorage.getItem(WELCOMED); } catch { return true; } }

export function startGangsheet(how: 'new' | 'auto', transfer: 'dtf' | 'uv') {
  const cfg = get().config;
  if (!cfg) return;
  const doc = emptyProject(transfer, cfg.builder.gapMm);
  if (how === 'new') {
    // by the metre, laid out by hand: a 58 × 100 cm sheet that grows as artwork is added
    doc.settings.sheet = { mode: 'roll' };
    doc.sheets = [blankRollSheet(cfg.pricing.transfers[transfer].byLength)];
  }
  const from = get().startFrom;
  if (from?.discard) {
    // started fresh without the last, unsaved changes: the server keeps the version saved before them
    try { localStorage.removeItem(localKey(from.id)); } catch { /* ignore */ }
  } else if (from && get().projectId === from.id && get().save.state !== 'saved') {
    // something changed after it was saved (e.g. a copy placed while the chooser was open): send it now
    // (the answer only ever applies to that gangsheet; its local copy is kept until it lands)
    void flushSave();
  }
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  set({
    projectId: null, rev: 0, doc, assets: {}, uploads: {}, past: [], future: [], selection: [], designSel: [], activeSheet: 0,
    save: { state: 'idle' }, submission: null, lastBuild: null, view: how === 'new' ? 'editor' : 'start',
    panel: null, reviewOpen: false, focus: false, designOpen: [], startOpen: false, startFrom: null, freshPrompt: null, meta: freshMeta()
  });
  lastSavedAt = null;
  try { localStorage.setItem(WELCOMED, '1'); } catch { /* ignore */ }
  if (from) {
    // the gangsheet it came from stays saved; say where, with the way back
    if (typeof location !== 'undefined' && location.pathname !== '/') navigate('/');
    const name = from.name || 'Your gangsheet';
    toast('“' + name + '” is saved in My gang sheets.', { action: { label: 'Open', run: () => navigate('/p/' + from.id) }, ms: 8000 });
  }
}
/** Close the chooser. A new gangsheet goes on with Auto Build; opened over a gangsheet, it goes back to it. */
export function closeStart() {
  set({ startOpen: false, startFrom: null });
  try { localStorage.setItem(WELCOMED, '1'); } catch { /* ignore */ }
}

/** "Place them myself": a by-the-metre sheet laid out by hand, with every design's copies put on it. */
export async function placeMyself() {
  const s = get();
  const cfg = s.config;
  if (!cfg) return;
  commit('Place them myself', d => {
    d.settings.sheet = { mode: 'roll' };
    if (!d.sheets.length) d.sheets.push(blankRollSheet(cfg.pricing.transfers[d.transfer].byLength) as Draft<Sheet>);
    d.layoutStale = false;
  });
  set({ view: 'editor', activeSheet: 0, selection: [] });
  await placeToMatch();
}

async function loadAssets(ids: string[]) {
  const want = ids.filter(id => id && !id.startsWith('pending:'));
  if (!want.length) return;
  const r = await api.assets(want);
  set(s => {
    const a = { ...s.assets };
    r.assets.forEach(x => { a[x.id] = x; });
    return { assets: a };
  });
}

/* ---------------------------------------------------------------- uploads */
const queue: { designId: string; file: File }[] = [];
let activeUploads = 0;
const touched = new Set<string>();           // designs whose size the customer set themselves
const placeWhenReady = new Set<string>();    // put one copy on the sheet once the file is ready (text)
export function markTouched(designId: string) { touched.add(designId); }

function extOf(name: string) { return (name.split('.').pop() || '').toLowerCase(); }

export async function addFiles(files: File[] | FileList, opts: { quiet?: boolean; place?: boolean; widthMm?: number; text?: TextSpec; onDesign?: (designId: string) => void } = {}) {
  // files dropped while the start chooser is open: that's starting with Auto Build (a new gangsheet, also
  // when the chooser was opened over another one)
  if (get().startOpen) { if (get().startFrom) startGangsheet('auto', get().doc.transfer); else closeStart(); }
  const s = get();
  const cfg = s.config;
  if (!cfg) return;
  const list = Array.from(files);
  if (!list.length) return;
  const accept = cfg.builder.upload.accept;
  const maxBytes = cfg.builder.upload.maxFileMb * 1048576;
  const ok: File[] = [];
  for (const f of list) {
    const ext = extOf(f.name);
    if (!accept.includes(ext) && !(f.type && /^image\/(png|jpeg|webp|svg\+xml|tiff)$|^application\/pdf$/.test(f.type))) {
      toast(f.name + ': ' + (ext ? ext.toUpperCase() + ' files aren’t supported' : 'unknown file type') + '. Use PNG, JPG, WEBP, SVG, PDF or TIFF.', { bad: true, ms: 6000 });
      continue;
    }
    if (f.size > maxBytes) { toast(f.name + ' is over ' + cfg.builder.upload.maxFileMb + ' MB.', { bad: true, ms: 6000 }); continue; }
    ok.push(f);
  }
  const room = cfg.builder.upload.maxFilesPerProject - s.doc.designs.length;
  if (ok.length > room) {
    toast('A gangsheet can hold ' + cfg.builder.upload.maxFilesPerProject + ' designs. Start another for the rest.', { bad: true });
    ok.splice(Math.max(0, room));
  }
  if (!ok.length) return;
  const entries: Record<string, UploadEntry> = {};
  const designs: Design[] = ok.map(f => {
    const id = newId('d');
    const localUrl = /^image\/(png|jpeg|webp|svg\+xml)$/.test(f.type) ? URL.createObjectURL(f) : undefined;
    entries[id] = { designId: id, name: f.name, progress: 0, status: 'queued', localUrl };
    queue.push({ designId: id, file: f });
    if (opts.widthMm) touched.add(id);       // a size the customer chose (text): keep it, the height follows the artwork
    const text = opts.text && TextSpecSchema.safeParse(opts.text).success ? { ...opts.text } : undefined;
    return { id, assetId: 'pending:' + id, name: f.name.replace(/\.[a-z0-9]+$/i, '') || 'Design', widthMm: opts.widthMm ? round1(opts.widthMm) : 100, heightMm: 100, quantity: 1, lockRotation: false, trim: true, ...(text ? { text } : {}) };
  });
  set(st => ({ uploads: { ...st.uploads, ...entries } }));
  // a design placed straight onto the sheet (text, or anything on a sheet laid out by hand) doesn't make the
  // layout out of date; if it can't fit, the checks say so
  const manual = isManualDoc(s.doc);
  commit(designs.length === 1 ? 'Add ' + designs[0].name : 'Add ' + designs.length + ' designs', d => { d.designs.push(...designs); }, { stale: !((opts.place || manual) && s.doc.sheets.length > 0) });
  if (s.view === 'start') set({ view: 'designs' });
  // by hand, the artwork goes onto the sheet: on phones keep the sheet in view rather than the list
  if (get().panel !== 'designs' && !opts.quiet && !(manual && !wide())) set({ panel: 'designs' });
  // local pixel size for the first estimate
  designs.forEach((d, i) => void localSize(d.id, ok[i]));
  if (opts.place) designs.forEach(d => placeWhenReady.add(d.id));
  designs.forEach(d => opts.onDesign?.(d.id));
  try { await ensureProject(); } catch (e) { toast((e as Error).message, { bad: true }); }
  pump();
}

/** Swap a design's artwork for a new file (edited text), keeping its width and every placed copy's width. */
const replacing = new Map<string, { designId: string; name: string; widthMm?: number; text?: TextSpec }>();
export async function replaceDesignFile(designId: string, file: File, opts: { name?: string; widthMm?: number; text?: TextSpec } = {}) {
  const d0 = get().doc.designs.find(x => x.id === designId);
  if (!d0) return;
  set(st => ({ uploads: { ...st.uploads, [designId]: { ...(st.uploads[designId] || { designId, name: file.name }), name: file.name, status: 'uploading', progress: 0, error: undefined } } }));
  try {
    const asset = await uploadFile(file, p => patchUpload(designId, { progress: p }));
    set(st => ({ assets: { ...st.assets, [asset.id]: asset } }));
    patchUpload(designId, { status: 'processing', progress: 1 });
    replacing.set(asset.id, { designId, name: opts.name || d0.name, widthMm: opts.widthMm, text: opts.text && TextSpecSchema.safeParse(opts.text).success ? { ...opts.text } : undefined });
    if (asset.status !== 'processing') finishReplace(asset);
    else pollAssets();
  } catch (e) {
    patchUpload(designId, { status: 'failed', error: (e as Error).message });
    toast('We couldn’t update that design: ' + (e as Error).message, { bad: true });
  }
}
function finishReplace(a: AssetInfo) {
  const r = replacing.get(a.id);
  if (!r) return;
  replacing.delete(a.id);
  if (a.status !== 'ready') { patchUpload(r.designId, { status: 'failed', error: a.error || undefined }); toast('We couldn’t read the new version.', { bad: true }); return; }
  patchUpload(r.designId, { status: 'ready' });
  const aspect = (a.widthPx * a.trim.w) / (a.heightPx * a.trim.h);
  commit('Edit ' + r.name, d => {
    const des = d.designs.find(x => x.id === r.designId);
    if (!des) return;
    const k = r.widthMm && des.widthMm ? r.widthMm / des.widthMm : 1;   // the customer changed the width: copies follow
    des.assetId = a.id;
    des.name = r.name;
    if (r.text) des.text = r.text;
    if (r.widthMm) des.widthMm = round1(r.widthMm);
    des.heightMm = round1(des.widthMm / aspect);
    d.sheets.forEach(sh => sh.items.forEach(it => {
      if (it.designId !== des.id) return;
      it.widthMm = round1(it.widthMm * k);
      it.heightMm = round1(it.widthMm / aspect);
    }));
  });
}

async function localSize(designId: string, f: File) {
  if (!/^image\/(png|jpeg|webp)$/.test(f.type) || typeof createImageBitmap !== 'function') return;
  try {
    const bmp = await createImageBitmap(f);
    const px = { w: bmp.width, h: bmp.height };
    bmp.close();
    set(s => (s.uploads[designId] ? { uploads: { ...s.uploads, [designId]: { ...s.uploads[designId], localPx: px } } } : {}));
    if (!touched.has(designId)) applyDefaultSize(designId, px.w, px.h, null);
  } catch { /* the server will measure it */ }
}

function patchUpload(designId: string, patch: Partial<UploadEntry>) {
  set(s => (s.uploads[designId] ? { uploads: { ...s.uploads, [designId]: { ...s.uploads[designId], ...patch } } } : {}));
}

function pump() {
  while (activeUploads < 3 && queue.length) {
    const job = queue.shift()!;
    activeUploads++;
    patchUpload(job.designId, { status: 'uploading', progress: 0 });
    let last = 0;
    uploadFile(job.file, p => { if (p - last > 0.02 || p === 1) { last = p; patchUpload(job.designId, { progress: p }); } })
      .then(asset => {
        set(s => ({ assets: { ...s.assets, [asset.id]: asset } }));
        patchUpload(job.designId, { status: 'processing', progress: 1 });
        silently(d => {
          const des = d.designs.find(x => x.id === job.designId);
          if (des) des.assetId = asset.id;
        });
        pollAssets();
      })
      .catch((e: Error) => {
        patchUpload(job.designId, { status: 'failed', error: e.message });
      })
      .finally(() => { activeUploads--; pump(); });
  }
}

let pollTimer: ReturnType<typeof setTimeout> | null = null;
function pollAssets() {
  if (pollTimer) return;
  const tick = async () => {
    pollTimer = null;
    const s = get();
    const ids = Object.values(s.assets).filter(a => a.status === 'processing').map(a => a.id);
    if (!ids.length) return;
    try {
      const r = await api.assets(ids);
      for (const a of r.assets) {
        if (a.status === 'processing') continue;
        set(st => ({ assets: { ...st.assets, [a.id]: a } }));
        if (replacing.has(a.id)) { finishReplace(a); continue; }
        const d = get().doc.designs.find(x => x.assetId === a.id);
        if (d) {
          patchUpload(d.id, { status: a.status === 'ready' ? 'ready' : 'failed', error: a.error || undefined });
          if (a.status === 'ready') {
            if (!touched.has(d.id)) applyDefaultSize(d.id, a.widthPx * a.trim.w, a.heightPx * a.trim.h, a);
            else keepAspect(d.id, a);
            if (isManualDoc(get().doc)) { placeWhenReady.delete(d.id); void placeToMatch(); }
            else if (placeWhenReady.delete(d.id) && get().doc.sheets.length) void addCopy(d.id);
            if (a.kind === 'pdf' && a.pages && a.pages > 1) toast(d.name + ': only page 1 of ' + a.pages + ' is used.', { ms: 6000 });
          }
        }
      }
    } catch { /* try again */ }
    if (Object.values(get().assets).some(a => a.status === 'processing')) pollTimer = setTimeout(tick, 900);
  };
  pollTimer = setTimeout(tick, 600);
}

/** Best-quality size: 300 DPI for pixels, the file's own size for vectors. */
function applyDefaultSize(designId: string, pxW: number, pxH: number, asset: AssetInfo | null) {
  const cfg = get().config;
  if (!cfg) return;
  const b = cfg.builder;
  const maxW = cfg.pricing.transfers[get().doc.transfer].byLength.widthMm - 2 * b.edgeMm;
  let w: number, h: number;
  if (asset && asset.kind !== 'raster') {
    const aspect = (asset.widthPx * asset.trim.w) / (asset.heightPx * asset.trim.h);
    const nat = asset.naturalMm ? asset.naturalMm.w * asset.trim.w : 100;
    w = nat; h = w / aspect;
  } else {
    w = (pxW / b.dpi) * MM_PER_IN; h = (pxH / b.dpi) * MM_PER_IN;
  }
  const fit = Math.min(1, maxW / w, (cfg.pricing.transfers[get().doc.transfer].byLength.maxLengthMm - 2 * b.edgeMm) / h);
  w *= fit; h *= fit;
  if (w < 10) { h *= 10 / w; w = 10; }
  silently(d => {
    const des = d.designs.find(x => x.id === designId);
    if (des) { des.widthMm = round1(w); des.heightMm = round1(h); }
  });
}

function keepAspect(designId: string, a: AssetInfo) {
  const aspect = (a.widthPx * a.trim.w) / (a.heightPx * a.trim.h);
  silently(d => {
    const des = d.designs.find(x => x.id === designId);
    if (des) des.heightMm = round1(des.widthMm / aspect);
  });
}

export function retryUpload(designId: string) {
  toast('Drop the file in again to retry.');
  removeDesigns([designId]);
}

export const round1 = (v: number) => Math.round(v * 10) / 10;

/** A wide screen keeps a drawer open beside the canvas. */
function wide() { return typeof window !== 'undefined' && window.innerWidth >= 1600; }

/** Auto size: each design at its best print size (300 DPI for pixels, its own size for vectors), within the sheet. */
export function autoSize(ids: string[]) {
  const s = get();
  const cfg = s.config;
  if (!cfg) return;
  const b = cfg.builder;
  const bl = cfg.pricing.transfers[s.doc.transfer].byLength;
  const maxW = bl.widthMm - 2 * b.edgeMm, maxH = bl.maxLengthMm - 2 * b.edgeMm;
  const sizes = new Map<string, { w: number; h: number }>();
  for (const d of s.doc.designs) {
    if (!ids.includes(d.id)) continue;
    const a = s.assets[d.assetId];
    if (!a || a.status !== 'ready') continue;
    const tw = d.trim ? a.trim.w : 1, th = d.trim ? a.trim.h : 1;
    let w: number, h: number;
    if (a.kind !== 'raster') {
      const aspect = (a.widthPx * tw) / (a.heightPx * th);
      w = a.naturalMm ? a.naturalMm.w * tw : d.widthMm; h = w / aspect;
    } else { w = (a.widthPx * tw / b.dpi) * MM_PER_IN; h = (a.heightPx * th / b.dpi) * MM_PER_IN; }
    const fit = Math.min(1, maxW / w, maxH / h);
    sizes.set(d.id, { w: round1(Math.max(5, w * fit)), h: round1(Math.max(5, h * fit)) });
  }
  if (!sizes.size) { toast('Wait for your designs to finish uploading first.', { bad: true }); return; }
  sizes.forEach((_v, id) => markTouched(id));
  commit(sizes.size === 1 ? 'Best size' : 'Best size for ' + sizes.size + ' designs', d => {
    for (const des of d.designs) { const z = sizes.get(des.id); if (z) { des.widthMm = z.w; des.heightMm = z.h; des.presetKey = undefined; } }
    resizeCopies(d, sizes.keys());
  }, { stale: true });
  toast(sizes.size === 1 ? 'Sized for the best print quality.' : sizes.size + ' designs sized for the best print quality.');
}

export function openPanel(p: PanelKey | null) { set(s => ({ panel: s.panel === p ? null : p, sideOpen: false, focus: p ? false : s.focus })); }

/* ------------------------------------------------------ workspace view
   None of these touch the gangsheet: they only change what's on screen. */
export function setCanvasBg(bg: CanvasBg) { set({ canvasBg: bg }); saveUi({ canvasBg: bg }); }
export function setSideHidden(hidden: boolean) { set({ sideHidden: hidden, focus: false }); saveUi({ sideHidden: hidden }); }
export function setFocus(on: boolean) { set(s => ({ focus: on, sideOpen: on ? false : s.sideOpen })); }
export function setLockAspect(on: boolean) { set({ lockAspect: on }); saveUi({ lockAspect: on }); }
export function setSnap(patch: Partial<State['snap']>) { set(st => ({ snap: { ...st.snap, ...patch } })); saveUi({ snap: { ...get().snap } }); }
export function setShowGuides(on: boolean) { set(st => ({ show: { ...st.show, guides: on } })); saveUi({ guides: on }); }
export function toggleDesignOpen(id: string) { set(s => ({ designOpen: s.designOpen.includes(id) ? s.designOpen.filter(x => x !== id) : [...s.designOpen, id] })); }

/* --------------------------------------------------------- design list */
export function aspectOf(d: Design): number {
  const a = get().assets[d.assetId];
  if (a && a.status === 'ready') return d.trim ? (a.widthPx * a.trim.w) / (a.heightPx * a.trim.h) : a.widthPx / a.heightPx;
  const u = get().uploads[d.id];
  if (u?.localPx) return u.localPx.w / u.localPx.h;
  return d.widthMm / d.heightMm;
}
/** "Stretched wider / taller than the artwork (+N %)" for a size ratio ÷ artwork ratio. */
export function stretchText(stretch: number): string {
  const pct = Math.round((Math.max(stretch, 1 / stretch) - 1) * 100);
  return `Stretched ${stretch > 1 ? 'wider' : 'taller'} than the artwork (+${pct} %).`;
}

/** Change a design's size. With Lock proportions on, the other side follows (keeping the design's current
    proportions); off, only the side you typed changes, and Auto nest builds the copies at exactly that size. */
export function setDesignSize(ids: string[], w: number | null, h: number | null, label = 'Change size', keep = get().lockAspect) {
  ids.forEach(markTouched);
  commit(label, d => {
    for (const des of d.designs) {
      if (!ids.includes(des.id)) continue;
      const ar = des.widthMm > 0 && des.heightMm > 0 ? des.widthMm / des.heightMm : aspectOf(des as Design);
      if (w !== null) { des.widthMm = round1(w); if (keep) des.heightMm = round1(w / ar); }
      else if (h !== null) { des.heightMm = round1(h); if (keep) des.widthMm = round1(h * ar); }
      des.presetKey = undefined;
    }
    resizeCopies(d, ids);
  }, { stale: true });
}

/** Back to the artwork's own proportions (keeping the width). */
export function resetDesignProportions(ids: string[]) {
  ids.forEach(markTouched);
  commit('Reset proportions', d => {
    for (const des of d.designs) if (ids.includes(des.id)) des.heightMm = round1(des.widthMm / aspectOf(des as Design));
    resizeCopies(d, ids);
  }, { stale: true });
}

export function applyPreset(ids: string[], key: string) {
  const p = get().config?.builder.sizePresets.find(x => x.key === key);
  if (!p) return;
  ids.forEach(markTouched);
  commit('Size: ' + p.name, d => {
    for (const des of d.designs) {
      if (!ids.includes(des.id)) continue;
      const ar = aspectOf(des as Design);
      let w = p.widthMm, h = w / ar;
      if (p.maxHeightMm && h > p.maxHeightMm) { h = p.maxHeightMm; w = h * ar; }
      des.widthMm = round1(w); des.heightMm = round1(h); des.presetKey = p.key;
    }
    resizeCopies(d, ids);
  }, { stale: true });
}

export function setQuantity(ids: string[], q: number): Promise<void> | void {
  const n = Math.max(0, Math.min(10000, Math.round(q) || 0));
  commit('Change quantity', d => { for (const des of d.designs) if (ids.includes(des.id)) des.quantity = n; }, { stale: true });
  // laid out by hand: the copies on the sheet follow (part of this same change)
  if (isManualDoc(get().doc)) return placeToMatch();
}

/* ------------------------------------------- gangsheets laid out by hand
   (isManualDoc: by the metre, "Start new gangsheet" or "Place them myself"). The design list and the sheet
   stay in step: sizes resize the copies, quantities add or take away copies, new artwork is placed.
   Placing happens after the change that asked for it (the free spots come from the layout worker), but
   without an undo step of its own: one undo takes back the change and its placing together. */

/** Inside a change: give every copy of these designs the design's size (in place, kept inside the margins). */
function resizeCopies(d: Draft<ProjectDoc>, ids: Iterable<string>) {
  if (!isManualDoc(d)) return;
  const want = new Set(ids);
  const maxL = get().config?.pricing.transfers[d.transfer].byLength.maxLengthMm;
  for (const sh of d.sheets) for (const it of sh.items) {
    if (!want.has(it.designId)) continue;
    const des = d.designs.find(x => x.id === it.designId);
    if (!des || (Math.abs(it.widthMm - des.widthMm) < 0.01 && Math.abs(it.heightMm - des.heightMm) < 0.01)) continue;
    it.widthMm = des.widthMm; it.heightMm = des.heightMm;
    Object.assign(it, clampInside(it, sh, maxL));
  }
}

function placedCount(doc: Pick<ProjectDoc, 'sheets'>, designId: string): number {
  return doc.sheets.reduce((n, sh) => n + sh.items.filter(i => i.designId === designId).length, 0);
}

/** Take away the copies over each design's quantity, the most recently placed first. Notes the sheets it
    took copies from. */
function removeExtras(d: Draft<ProjectDoc>, touchedSheets?: Set<string>) {
  for (const des of d.designs) {
    let extra = placedCount(d, des.id) - des.quantity;
    for (let si = d.sheets.length - 1; si >= 0 && extra > 0; si--) {
      const items = d.sheets[si].items;
      for (let k = items.length - 1; k >= 0 && extra > 0; k--) {
        if (items[k].designId !== des.id) continue;
        items.splice(k, 1); extra--;
        touchedSheets?.add(d.sheets[si].id);
      }
    }
  }
}

const MAX_SHEETS = 100;
interface Placement { sheet: number; item: Item }

/** Free spots for `count` more upright copies of a design: top-down in the free space, letting the
    by-the-metre sheet grow to its longest, then on a new sheet. */
async function placeCopies(doc: ProjectDoc, designId: string, count: number, startSheet: number): Promise<{ doc: ProjectDoc; added: Placement[]; left: number; why: 'too_big' | 'sheets' | null }> {
  const cfg = get().config!;
  const bl = cfg.pricing.transfers[doc.transfer].byLength;
  const des = doc.designs.find(x => x.id === designId);
  const added: Placement[] = [];
  if (!des || count <= 0) return { doc, added, left: Math.max(0, count), why: null };
  let work = doc;
  let si = Math.max(0, Math.min(startSheet, work.sheets.length - 1));
  while (count > 0) {
    if (si >= MAX_SHEETS) return { doc: work, added, left: count, why: 'sheets' };
    if (!work.sheets[si]) work = produce(work, d => { d.sheets.push(blankRollSheet(bl) as Draft<Sheet>); });
    const sheet = work.sheets[si];
    // a roll can grow to its longest: let the fill use all of it (the length follows the content after)
    const probe = sheet.kind === 'roll' ? produce(work, d => { d.sheets[si].lengthMm = bl.maxLengthMm; }) : work;
    const r = await nest.fill(probe, cfg.builder, si, designId, des.widthMm, des.heightMm, count, false);
    if (r.count) {
      const at = si;
      work = produce(work, d => { d.sheets[at].items.push(...(r.items as Draft<Item>[])); });
      r.items.forEach(item => added.push({ sheet: at, item }));
      count -= r.count;
    }
    if (count > 0) {
      if (!r.count && !sheet.items.length) return { doc: work, added, left: count, why: 'too_big' };
      si++;
    }
  }
  return { doc: work, added, left: 0, why: null };
}

interface Plan { add: Placement[]; tooBig: number; limit: number }

/** What to place so every design has as many copies as its quantity (worked out on `base`). */
async function planPlacements(base: ProjectDoc, s: State): Promise<Plan> {
  const cfg = s.config!;
  let work = produce(base, d => removeExtras(d));
  const plan: Plan = { add: [], tooBig: 0, limit: 0 };
  let total = work.sheets.reduce((n, sh) => n + sh.items.length, 0);
  for (const des of work.designs) {
    const ready = !des.assetId.startsWith('pending:') && s.assets[des.assetId]?.status === 'ready';
    let want = des.quantity - placedCount(work, des.id);
    if (want <= 0 || !ready) continue;                  // a file still uploading is placed when it's ready
    const room = Math.max(0, cfg.builder.maxItems - total);
    if (want > room) { plan.limit += want - room; want = room; }
    if (!want) continue;
    const r = await placeCopies(work, des.id, want, s.activeSheet);
    work = r.doc;
    total += r.added.length;
    plan.add.push(...r.added);
    if (r.why === 'too_big') plan.tooBig += r.left; else plan.limit += r.left;
  }
  return plan;
}

/** Apply a plan to the gangsheet as it is now (it may have changed meanwhile): take away extra copies,
    add planned copies a design still needs, drop a sheet that taking copies away left empty. */
function applyPlacements(plan: Plan) {
  const cfg = get().config;
  if (!cfg) return;
  silently(d => {
    if (!isManualDoc(d)) return;
    const emptied = new Set<string>();
    removeExtras(d, emptied);
    const need = new Map(d.designs.map(x => [x.id, x.quantity - placedCount(d, x.id)]));
    const bl = cfg.pricing.transfers[d.transfer].byLength;
    for (const a of plan.add) {
      const n = need.get(a.item.designId) || 0;
      if (n <= 0) continue;
      while (d.sheets.length <= a.sheet) d.sheets.push(blankRollSheet(bl) as Draft<Sheet>);
      d.sheets[a.sheet].items.push(a.item as Draft<Item>);
      need.set(a.item.designId, n - 1);
    }
    if (emptied.size) d.sheets = d.sheets.filter((sh, i) => i === 0 || sh.items.length > 0 || !emptied.has(sh.id));
  });
  set(st => ({ activeSheet: Math.max(0, Math.min(st.activeSheet, get().doc.sheets.length - 1)), selection: keepSel(get().doc, st.selection) }));
  if (plan.tooBig) toast(plan.tooBig === 1 ? 'One copy is too big for the sheet. Make the design smaller.' : plan.tooBig + ' copies are too big for the sheet. Make the design smaller.', { bad: true, ms: 6000 });
  if (plan.limit) toast('A gangsheet can hold ' + cfg.builder.maxItems + ' transfers (' + MAX_SHEETS + ' sheets). ' + plan.limit + ' didn’t go on. Start another gangsheet for the rest.', { bad: true, ms: 8000 });
}

let placing: Promise<void> | null = null;
let placeAgain = false;
/** Make the copies on a gangsheet laid out by hand match the design list (quantities are read when it
    runs, so quick + / − clicks all count). Calls while it's busy are folded into one more pass. */
export function placeToMatch(): Promise<void> {
  if (placing) { placeAgain = true; return placing; }
  placing = (async () => {
    try {
      do {
        placeAgain = false;
        for (let attempt = 0; attempt < 5; attempt++) {
          const s = get();
          const base = s.doc, pid = s.projectId;
          if (!s.config || !isManualDoc(base)) break;
          const plan = await planPlacements(base, s);
          if (get().projectId !== pid) break;            // another gangsheet opened meanwhile
          // changed while we worked it out (a drag, an upload finishing): work it out again; in the end,
          // apply it to the latest version anyway (the counts are checked again there)
          if (get().doc !== base && attempt < 4) continue;
          applyPlacements(plan);
          break;
        }
      } while (placeAgain);
    } catch (e) {
      toast('We couldn’t place your designs on the sheet: ' + ((e as Error).message || 'please try again') + '.', { bad: true, ms: 7000 });
    } finally {
      placing = null;
    }
  })();
  return placing;
}
/** Resolves when nothing is waiting to be placed (ordering waits for it). */
export function placementsSettled(): Promise<void> { return placing ?? Promise.resolve(); }

export function setDesignFlag(id: string, key: 'trim' | 'lockRotation', v: boolean) {
  commit(key === 'trim' ? (v ? 'Trim empty space' : 'Keep empty space') : (v ? 'Lock rotation' : 'Allow rotation'), d => {
    const des = d.designs.find(x => x.id === id);
    if (!des) return;
    if (key === 'trim') {
      const a = get().assets[des.assetId];
      des.trim = v;
      if (a && a.status === 'ready') {
        // keep the artwork the same physical size: grow/shrink the box around it
        const fx = v ? a.trim.w : 1 / a.trim.w, fy = v ? a.trim.h : 1 / a.trim.h;
        des.widthMm = round1(des.widthMm * fx);
        des.heightMm = round1(des.heightMm * fy);
      }
      resizeCopies(d, [id]);
    } else des.lockRotation = v;
  }, { stale: true });
}

export function renameDesign(id: string, name: string) {
  silently(d => { const des = d.designs.find(x => x.id === id); if (des) des.name = name.slice(0, 200); });
}

export function removeDesigns(ids: string[]) {
  if (!ids.length) return;
  const names = get().doc.designs.filter(d => ids.includes(d.id)).map(d => d.name);
  commit(ids.length === 1 ? 'Remove ' + names[0] : 'Remove ' + ids.length + ' designs', d => {
    d.designs = d.designs.filter(x => !ids.includes(x.id));
    d.sheets.forEach(s => { s.items = s.items.filter(i => !ids.includes(i.designId)); });
  });
  set(s => ({ designSel: s.designSel.filter(x => !ids.includes(x)), selection: keepSel(get().doc, s.selection) }));
  toast(ids.length === 1 ? 'Removed ' + names[0] + '.' : 'Removed ' + ids.length + ' designs.', { action: { label: 'Undo', run: undo } });
}

export function duplicateDesigns(ids: string[]) {
  const copies: Design[] = [];
  commit(ids.length === 1 ? 'Duplicate design' : 'Duplicate ' + ids.length + ' designs', d => {
    for (const des of d.designs.filter(x => ids.includes(x.id))) {
      const c = { ...(des as Design), id: newId('d'), name: des.name + ' copy' };
      copies.push(c);
      d.designs.push(c);
    }
  }, { stale: true });
  copies.forEach(c => markTouched(c.id));
  if (isManualDoc(get().doc)) void placeToMatch();
}

export function setTransfer(t: 'dtf' | 'uv') {
  commit(t === 'uv' ? 'UV DTF stickers' : 'DTF transfers', d => { d.transfer = t; });
}
export function setSheetChoice(c: SheetChoice) {
  const cfg = get().config;
  const nothingPlaced = !get().doc.sheets.some(sh => sh.items.length);
  commit('Change sheet', d => {
    d.settings.sheet = c;
    if (nothingPlaced && cfg) {
      // nothing on a sheet yet: simply start on the new format (by the metre = a blank sheet to arrange)
      d.sheets = c.mode === 'roll' ? [blankRollSheet(cfg.pricing.transfers[d.transfer].byLength) as Draft<Sheet>] : [];
      d.layoutStale = false;
    }
  }, { stale: nothingPlaced ? false : 'force' });
  if (nothingPlaced && c.mode === 'roll' && get().doc.designs.length) void placeToMatch();
}
export function setGap(mm: number) {
  const cfg = get().config;
  const v = Math.max(cfg?.builder.minGapMm ?? 0, cfg ? inkGapFor(get().doc, cfg.builder) : 0, Math.min(50, mm));
  commit('Change spacing', d => { d.settings.gapMm = v; }, { stale: true });
}
export function setAllowRotation(v: boolean) {
  commit(v ? 'Allow rotation' : 'No rotation', d => { d.settings.allowRotation = v; }, { stale: true });
}
export function setUnit(u: 'mm' | 'cm' | 'in') {
  silently(d => { d.settings.unit = u; });
}
export function rename(name: string) {
  silently(d => { d.name = name.slice(0, 120); });
}
export function acknowledge(key: string, v = true) {
  commit(v ? 'Accept warning' : 'Undo accept', d => {
    d.acknowledged = d.acknowledged.filter(k => k !== key);
    if (v) d.acknowledged.push(key);
  });
}

/* ------------------------------------------------------------- building */
const BUILD_LINES = ['Finding the best layout…', 'Arranging your transfers to use the least film…', 'Checking every gap and edge…'];

export async function build(kind: 'build' | 'renest' = 'build') {
  const s = get();
  const cfg = s.config;
  if (!cfg) return;
  const ready = s.doc.designs.filter(d => !d.assetId.startsWith('pending:') && s.assets[d.assetId]?.status === 'ready');
  const count = kind === 'build' ? ready.reduce((n, d) => n + d.quantity, 0) : s.doc.sheets.reduce((n, sh) => n + sh.items.length, 0);
  if (!count) { toast(kind === 'build' ? 'Set a quantity of at least 1 on a design first.' : 'There’s nothing on the sheet to arrange.', { bad: true }); return; }
  set({ busy: { title: kind === 'build' ? 'Building your gangsheet…' : 'Re-nesting your gangsheet…', detail: 'Arranging ' + count + ' transfer' + (count === 1 ? '' : 's') + ' to use the least amount of film.', progress: null } });
  const t0 = performance.now();
  try {
    const doc = kind === 'build' ? { ...s.doc, designs: ready } : s.doc;
    const r = kind === 'build' ? await nest.build(doc, cfg.builder, cfg.pricing as any) : await nest.renest(doc, cfg.builder, cfg.pricing as any);
    // let the branded wait be seen for a moment: it reassures more than a flicker
    const wait = 700 - (performance.now() - t0);
    if (wait > 0) await new Promise(res => setTimeout(res, wait));
    commit(kind === 'build' ? 'Build my gangsheet' : 'Re-nest gangsheet', d => {
      d.sheets = r.sheets as any;
      d.layoutStale = false;
    });
    set({ lastBuild: { overall: r.overall, utilisation: r.utilisation, considered: r.considered, unplaced: r.unplaced, chosen: r.chosen }, activeSheet: 0, selection: [] });
    if (get().view === 'designs' || get().view === 'start') set({ view: 'review' });
    if (!wide()) set({ panel: null });
    const pct = Math.round(r.overall * 100);
    const tooBig = r.unplaced.filter(u => u.reason === 'too_big');
    if (tooBig.length) toast(tooBig.length === 1 ? 'One design is too big for the sheet. Make it smaller and rebuild.' : tooBig.length + ' designs are too big for the sheet.', { bad: true, ms: 7000 });
    else if (r.bumped) toast('We made it a ' + formatMetres(r.bumped.toMm) + ' sheet: it costs ' + formatMoney(r.bumped.saving) + ' less than ' + formatMetres(r.bumped.fromMm) + ', and leaves room for more designs.', { ms: 7000 });
    else if (r.chosen.kind === 'fixed' && r.sheets.length === 1) {
      const f = cfg.pricing.transfers[get().doc.transfer].fixedSheets.find(x => x.key === r.chosen.key);
      toast('Everything fits on one ' + (f ? f.name : 'sheet') + ' (' + Math.round(r.sheets[0].widthMm / 10) + ' × ' + Math.round(r.sheets[0].lengthMm / 10) + ' cm). The cheapest option.');
    } else toast('Gangsheet optimised: ' + pct + '% film utilisation.');
  } catch (e) {
    toast('We couldn’t build that layout: ' + (e as Error).message, { bad: true });
  } finally {
    set({ busy: null });
  }
}

/** Rough film needed before building (for the estimate). */
export function estimateLengthMm(doc: ProjectDoc, cfg: PublicConfig): number {
  const g = cfg ? layoutGap(doc, cfg.builder) : doc.settings.gapMm;
  const W = cfg.pricing.transfers[doc.transfer].byLength.widthMm - 2 * cfg.builder.edgeMm + g;
  let area = 0;
  for (const d of doc.designs) area += (d.widthMm + g) * (d.heightMm + g) * d.quantity;
  return area / W / 0.86 + 2 * cfg.builder.edgeMm;
}

/* ---------------------------------------------------------------- editor */
export function select(ids: string[], add = false) {
  set(s => ({ selection: add ? [...new Set([...s.selection, ...ids])] : ids }));
}
export function toggleSelect(id: string) {
  set(s => ({ selection: s.selection.includes(id) ? s.selection.filter(x => x !== id) : [...s.selection, id] }));
}
export function clearSelection() { set({ selection: [] }); }
export function setActiveSheet(i: number) { set({ activeSheet: i, selection: [] }); }

function sheetOf(d: Draft<ProjectDoc>, itemId: string): Draft<Sheet> | undefined {
  return d.sheets.find(s => s.items.some(i => i.id === itemId));
}
function forSelected(d: Draft<ProjectDoc>, ids: string[], fn: (it: Draft<Item>, s: Draft<Sheet>) => void) {
  for (const s of d.sheets) for (const it of s.items) if (ids.includes(it.id)) fn(it, s);
}

export function updateItems(label: string, ids: string[], fn: (it: Draft<Item>, s: Draft<Sheet>) => void) {
  commit(label, d => forSelected(d, ids, fn));
}

/** Keep an item's box inside its sheet. */
/** Keep a copy inside the PRINTABLE area: the sheet minus the edge margin (a by-the-metre sheet can grow
    down to its maximum length). Something bigger than the area is centred on it (the checks flag it). */
export function clampInside(it: Pick<Item, 'cx' | 'cy' | 'widthMm' | 'heightMm' | 'rotation'>, s: Pick<Sheet, 'widthMm' | 'lengthMm' | 'kind'>, maxLength?: number, edge = get().config?.builder.edgeMm ?? 0) {
  const b = itemBox(it);
  const L = s.kind === 'roll' ? Math.max(s.lengthMm, maxLength || s.lengthMm) : s.lengthMm;
  const W = s.widthMm;
  let cx = it.cx, cy = it.cy;
  cx = b.w <= W - 2 * edge ? Math.min(Math.max(cx, edge + b.w / 2), W - edge - b.w / 2) : W / 2;
  cy = b.h <= L - 2 * edge ? Math.min(Math.max(cy, edge + b.h / 2), L - edge - b.h / 2) : Math.max(cy, b.h / 2);
  return { cx, cy };
}

/** clampInside, and if the copy is bigger than the printable area, make it smaller to fit (keeping its proportions). */
export function fitInside<T extends Pick<Item, 'cx' | 'cy' | 'widthMm' | 'heightMm' | 'rotation'>>(it: T, s: Pick<Sheet, 'widthMm' | 'lengthMm' | 'kind'>, maxLength?: number): T {
  const edge = get().config?.builder.edgeMm ?? 0;
  const L = s.kind === 'roll' ? Math.max(s.lengthMm, maxLength || s.lengthMm) : s.lengthMm;
  const b = itemBox(it);
  const k = Math.min(1, (s.widthMm - 2 * edge) / b.w, (L - 2 * edge) / b.h);
  const out = k < 1 ? { ...it, widthMm: round2(it.widthMm * k), heightMm: round2(it.heightMm * k) } : { ...it };
  return { ...out, ...clampInside(out, s, maxLength, edge) };
}
function round2(v: number) { return Math.floor(v * 100) / 100; }

export function deleteSelected() {
  const ids = get().selection;
  if (!ids.length) return;
  commit(ids.length === 1 ? 'Delete' : 'Delete ' + ids.length + ' transfers', d => {
    const byDesign = new Map<string, number>();
    d.sheets.forEach(s => s.items.forEach(i => { if (ids.includes(i.id)) byDesign.set(i.designId, (byDesign.get(i.designId) || 0) + 1); }));
    d.sheets.forEach(s => { s.items = s.items.filter(i => !ids.includes(i.id)); });
    d.designs.forEach(des => { const n = byDesign.get(des.id); if (n) des.quantity = Math.max(0, des.quantity - n); });
  });
  set({ selection: [] });
}

/** Duplicate the selection n times, placing copies in free space when possible. */
export async function duplicateSelected(times = 1) {
  const s = get();
  const cfg = s.config;
  const ids = s.selection;
  if (!cfg || !ids.length) return;
  const sheetIdx = s.activeSheet;
  const sheet = s.doc.sheets[sheetIdx];
  const src = sheet.items.filter(i => ids.includes(i.id));
  if (!src.length) return;
  const copies: Item[] = [];
  let work: ProjectDoc = s.doc;
  for (let k = 0; k < times; k++) {
    for (const it of src) {
      // a free spot for this copy's own box (same rotation, same size)
      const b = itemBox(it);
      const fill = await nest.fill(work, cfg.builder, sheetIdx, it.designId, b.w, b.h, 1, false);
      let c: Item;
      if (fill.count) {
        const f = fill.items[0];
        c = { ...it, id: newId('i'), cx: f.cx, cy: f.cy, locked: false };
      } else {
        // no room: drop it below everything (roll sheets grow; others get a preflight warning)
        const bottom = Math.max(0, ...work.sheets[sheetIdx].items.map(x => itemBox(x).y + itemBox(x).h));
        c = { ...it, id: newId('i'), cx: it.cx, cy: bottom + (s.config ? layoutGap(s.doc, s.config.builder) : s.doc.settings.gapMm) + b.h / 2, locked: false };
      }
      copies.push(c);
      work = produce(work, d => { d.sheets[sheetIdx].items.push(c as Draft<Item>); });
    }
  }
  commit(copies.length === 1 ? 'Duplicate' : 'Duplicate × ' + copies.length, d => {
    d.sheets[sheetIdx].items.push(...(copies as Draft<Item>[]));
    const byDesign = new Map<string, number>();
    copies.forEach(c => byDesign.set(c.designId, (byDesign.get(c.designId) || 0) + 1));
    d.designs.forEach(des => { const n = byDesign.get(des.id); if (n) des.quantity += n; });
  });
  set({ selection: copies.map(c => c.id) });
  const outside = copies.some(c => { const b = itemBox(c); return b.y + b.h > get().doc.sheets[sheetIdx].lengthMm + 0.1; });
  if (outside) toast('Not everything fitted. Check the preflight, or press Re-nest.', { bad: true });
}

/** Add n copies of a design into the free space of the active sheet. */
export async function fillWith(designId: string, widthMm: number, heightMm: number, limit?: number): Promise<number> {
  const s = get();
  const cfg = s.config;
  if (!cfg || !s.doc.sheets[s.activeSheet]) return 0;
  const r = await nest.fill(s.doc, cfg.builder, s.activeSheet, designId, widthMm, heightMm, limit);
  if (limit === undefined) return r.count;
  if (!r.count) return 0;
  const idx = s.activeSheet;
  commit('Fill empty space × ' + r.count, d => {
    d.sheets[idx].items.push(...(r.items as Draft<Item>[]));
    const des = d.designs.find(x => x.id === designId);
    if (des) des.quantity += r.count;
  });
  set({ selection: r.items.map(i => i.id) });
  return r.count;
}

/** Add one copy of a design to the active sheet (library click / drop). */
export async function addCopy(designId: string, at?: { cx: number; cy: number }) {
  const s = get();
  const cfg = s.config;
  const des = s.doc.designs.find(d => d.id === designId);
  if (!cfg || !des) return;
  if (!s.doc.sheets.length) {
    addSheet();
  }
  const st = get();
  const idx = st.activeSheet;
  const sheet = st.doc.sheets[idx];
  let item: Item;
  if (at) {
    item = { id: newId('i'), designId, cx: at.cx, cy: at.cy, widthMm: des.widthMm, heightMm: des.heightMm, rotation: 0, flipX: false, flipY: false, locked: false };
    Object.assign(item, clampInside(item, sheet, cfg.pricing.transfers[st.doc.transfer].byLength.maxLengthMm));
  } else {
    const r = await nest.fill(st.doc, cfg.builder, idx, designId, des.widthMm, des.heightMm, 1);
    if (r.count) item = r.items[0];
    else {
      const bottom = Math.max(0, ...sheet.items.map(x => itemBox(x).y + itemBox(x).h));
      item = { id: newId('i'), designId, cx: sheet.widthMm / 2, cy: bottom + (st.config ? layoutGap(st.doc, st.config.builder) : st.doc.settings.gapMm) + des.heightMm / 2, widthMm: des.widthMm, heightMm: des.heightMm, rotation: 0, flipX: false, flipY: false, locked: false };
    }
  }
  commit('Add ' + des.name, d => {
    d.sheets[idx].items.push(item as Draft<Item>);
    // the quantity follows what's placed (a copy that fills a shortfall doesn't add one)
    const x = d.designs.find(z => z.id === designId);
    const placed = d.sheets.reduce((n, sh) => n + sh.items.filter(i => i.designId === designId).length, 0);
    if (x && placed > x.quantity) x.quantity = placed;
  });
  set({ selection: [item.id] });
}

export function addSheet() {
  const s = get();
  const cfg = s.config;
  if (!cfg) return;
  const tp = cfg.pricing.transfers[s.doc.transfer];
  const c = s.doc.settings.sheet;
  let sheet: Sheet;
  if (c.mode === 'fixed') {
    const f = tp.fixedSheets.find(x => x.key === c.key) || tp.fixedSheets[0];
    sheet = { id: newId('s'), kind: 'fixed', fixedKey: f.key, widthMm: f.widthMm, lengthMm: f.lengthMm, items: [] };
  } else if (c.mode === 'length') {
    sheet = { id: newId('s'), kind: 'length', widthMm: tp.byLength.widthMm, lengthMm: c.lengthMm, items: [] };
  } else {
    sheet = { id: newId('s'), kind: 'roll', widthMm: tp.byLength.widthMm, lengthMm: tp.byLength.minLengthMm, items: [] };
  }
  commit('Add sheet', d => { d.sheets.push(sheet as Draft<Sheet>); });
  set({ activeSheet: get().doc.sheets.length - 1, selection: [] });
}

export function removeSheet(i: number) {
  const s = get();
  const sh = s.doc.sheets[i];
  if (!sh) return;
  commit('Remove sheet ' + (i + 1), d => {
    const byDesign = new Map<string, number>();
    sh.items.forEach(it => byDesign.set(it.designId, (byDesign.get(it.designId) || 0) + 1));
    d.sheets.splice(i, 1);
    d.designs.forEach(des => { const n = byDesign.get(des.id); if (n) des.quantity = Math.max(0, des.quantity - n); });
  });
  set({ activeSheet: Math.max(0, Math.min(i, get().doc.sheets.length - 1)), selection: [] });
}

export function layer(where: 'front' | 'back' | 'forward' | 'backward') {
  const ids = get().selection;
  const idx = get().activeSheet;
  const sheet = get().doc.sheets[idx];
  if (!ids.length || !sheet) return;
  const order = layerOrder(sheet.items.map(i => i.id), ids, where);
  // already at the front (or back): nothing changes, so no empty undo step
  if (order.join() === sheet.items.map(i => i.id).join()) return;
  commit({ front: 'Bring to front', back: 'Send to back', forward: 'Bring forward', backward: 'Send backward' }[where], d => {
    const s = d.sheets[idx];
    if (!s) return;
    const byId = new Map(s.items.map(i => [i.id, i]));
    s.items = order.map(id => byId.get(id)!).filter(Boolean);
  });
}
/** The sheet's drawing order (ids, back to front) after moving the selected ones. */
export function layerOrder(all: string[], ids: string[], where: 'front' | 'back' | 'forward' | 'backward'): string[] {
  const sel = all.filter(id => ids.includes(id)), rest = all.filter(id => !ids.includes(id));
  if (where === 'front') return [...rest, ...sel];
  if (where === 'back') return [...sel, ...rest];
  const arr = [...all];
  const order = where === 'forward' ? [...arr.keys()].reverse() : [...arr.keys()];
  for (const i of order) {
    if (!ids.includes(arr[i])) continue;
    const j = where === 'forward' ? i + 1 : i - 1;
    if (j < 0 || j >= arr.length || ids.includes(arr[j])) continue;
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function align(how: 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom') {
  const s = get();
  const ids = s.selection;
  const sheet = s.doc.sheets[s.activeSheet];
  if (!sheet || !ids.length) return;
  const items = sheet.items.filter(i => ids.includes(i.id) && !i.locked);
  const boxes = items.map(itemBox);
  // one item aligns to the sheet; several align to each other
  const ref = items.length === 1 ? { x: 0, y: 0, w: sheet.widthMm, h: sheet.lengthMm } : (() => {
    const x0 = Math.min(...boxes.map(b => b.x)), y0 = Math.min(...boxes.map(b => b.y));
    const x1 = Math.max(...boxes.map(b => b.x + b.w)), y1 = Math.max(...boxes.map(b => b.y + b.h));
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  })();
  const edge = items.length === 1 ? (s.config?.builder.edgeMm ?? 0) : 0;
  commit('Align', d => forSelected(d, items.map(i => i.id), it => {
    const b = itemBox(it);
    if (how === 'left') it.cx = ref.x + edge + b.w / 2;
    if (how === 'right') it.cx = ref.x + ref.w - edge - b.w / 2;
    if (how === 'hcenter') it.cx = ref.x + ref.w / 2;
    if (how === 'top') it.cy = ref.y + edge + b.h / 2;
    if (how === 'bottom') it.cy = ref.y + ref.h - edge - b.h / 2;
    if (how === 'vcenter') it.cy = ref.y + ref.h / 2;
  }));
}

export function distribute(axis: 'h' | 'v') {
  const s = get();
  const sheet = s.doc.sheets[s.activeSheet];
  const items = sheet?.items.filter(i => s.selection.includes(i.id) && !i.locked) || [];
  if (items.length < 3) { toast('Select three or more to distribute.'); return; }
  const withBox = items.map(i => ({ i, b: itemBox(i) })).sort((a, b) => (axis === 'h' ? a.b.x - b.b.x : a.b.y - b.b.y));
  const first = withBox[0].b, last = withBox[withBox.length - 1].b;
  const span = axis === 'h' ? last.x + last.w - first.x : last.y + last.h - first.y;
  const total = withBox.reduce((a, x) => a + (axis === 'h' ? x.b.w : x.b.h), 0);
  const gap = (span - total) / (withBox.length - 1);
  let pos = axis === 'h' ? first.x : first.y;
  const target = new Map<string, number>();
  for (const x of withBox) {
    const size = axis === 'h' ? x.b.w : x.b.h;
    target.set(x.i.id, pos + size / 2);
    pos += size + gap;
  }
  commit(axis === 'h' ? 'Distribute horizontally' : 'Distribute vertically', d => forSelected(d, [...target.keys()], it => {
    if (axis === 'h') it.cx = target.get(it.id)!; else it.cy = target.get(it.id)!;
  }));
}

export function nudge(dx: number, dy: number) {
  const s = get();
  const ids = s.selection;
  if (!ids.length) return;
  const maxL = s.config?.pricing.transfers[s.doc.transfer].byLength.maxLengthMm;
  commit('Move', d => forSelected(d, ids, (it, sh) => {
    if (it.locked) return;
    const p = clampInside({ ...it, cx: it.cx + dx, cy: it.cy + dy }, sh, maxL);
    it.cx = p.cx; it.cy = p.cy;
  }));
}

export function selectAll() {
  const s = get();
  const sheet = s.doc.sheets[s.activeSheet];
  if (sheet) set({ selection: sheet.items.map(i => i.id) });
}

/* --------------------------------------------------------------- ordering */
export async function order(): Promise<SubmissionInfo | null> {
  const s = get();
  if (!s.projectId) return null;
  const pending = Object.values(s.uploads).some(u => u.status === 'queued' || u.status === 'uploading' || u.status === 'processing');
  if (pending) { toast('Wait for your uploads to finish first.', { bad: true }); return null; }
  set({ busy: { title: 'Checking your gangsheet…', detail: 'Making sure everything is ready to print.' } });
  try {
    await placementsSettled();
    await flushSave();
    if (get().save.state === 'conflict') return null;
    if (get().save.state === 'error') throw new Error(get().save.error || 'We couldn’t save your gangsheet.');
    const r = await api.submit(get().projectId as string, get().rev);
    set({ submission: r.submission });
    return r.submission;
  } catch (e) {
    toast((e as Error).message, { bad: true, ms: 7000 });
    return null;
  } finally {
    set({ busy: null });
  }
}

export async function refreshSubmission(): Promise<SubmissionInfo | null> {
  const sub = get().submission;
  if (!sub) return null;
  const r = await api.submission(sub.id);
  set({ submission: r.submission });
  return r.submission;
}

export function stats(doc: ProjectDoc) {
  return doc.sheets.map(sheetStats);
}

/* ------------------------------------------------- Save & Email My Gangsheet (0.3e Stage B)
   A 6-digit code proves an email address; then the gangsheets saved to it open on any device that proves
   the same address. Off unless the server switches it on for this browser. */
export function emailOn(): boolean { return get().config?.email?.mode === 'on'; }

export async function loadEmailSession(): Promise<void> {
  if (!emailOn()) { set({ email: null }); return; }
  try { set({ email: await api.emailSession() }); } catch { set({ email: { signedIn: false } }); }
}

export function openEmailFlow(purpose: 'save' | 'find') {
  if (!emailOn()) { if (purpose === 'find') navigate('/my'); return; }
  set({ emailFlow: { purpose } });
}

/** Save & Email on the open gangsheet: straight away when signed in (no code), else the code window. */
export async function saveOpenToEmail(): Promise<void> {
  const s = get();
  if (!s.doc.designs.length) { toast('Add a design to your gangsheet first.', { bad: true }); return; }
  await placementsSettled();
  if (!get().projectId) { try { await ensureProject(); } catch (e) { toast((e as Error).message, { bad: true }); return; } }
  await flushSave();
  if (get().email?.signedIn) {
    try {
      const r = await api.saveToEmail(get().projectId!);
      set(st => ({ meta: { ...st.meta, savedTo: r.savedTo, emailedAt: r.confirmation === 'sent' ? new Date().toISOString() : st.meta.emailedAt } }));
      if (r.confirmation === 'sent') toast('Saved to ' + r.savedTo + '. We’ve emailed you the steps to open it on any device.', { ms: 7000 });
      else if (r.confirmation === 'already') toast('Saved to ' + r.savedTo + '.');
      else toast('Saved to ' + r.savedTo + '. ' + (r.confirmationMessage || ''), { bad: true, ms: 9000 });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 401) { await loadEmailSession(); set({ emailFlow: { purpose: 'save' } }); }
      else toast(err.message, { bad: true, ms: 7000 });
    }
    return;
  }
  set({ emailFlow: { purpose: 'save' } });
}

/** After a correct code: this browser is signed in (and, for Save & Email, the gangsheet is saved to it). */
export function emailVerified(r: { session: EmailSessionInfo; saved: { projectId: string; savedTo: string } | null }) {
  set(st => ({
    email: { ...r.session, mode: 'on' },
    meta: r.saved && r.saved.projectId === st.projectId ? { ...st.meta, savedTo: r.saved.savedTo, emailedAt: new Date().toISOString() } : st.meta
  }));
}

export async function signOutEmail(everywhere: boolean): Promise<void> {
  try {
    await api.emailSignout(everywhere);
  } catch (e) { toast((e as Error).message, { bad: true }); return; }
  set({ email: { signedIn: false, mode: 'on' } });
  toast(everywhere ? 'Signed out on every device.' : 'Signed out on this device.');
  // a gangsheet open here only through the email can't be opened any more
  const s = get();
  if (s.projectId && !s.meta.madeHere) { await flushSave().catch(() => {}); newProject(); navigate('/my'); }
}

/** Remove from my email: the gangsheet stays with the browser that made it. Nothing is deleted. */
export async function removeFromEmail(id: string): Promise<boolean> {
  try {
    const r = await api.removeFromEmail(id);
    if (get().projectId === id) {
      set(st => ({ meta: { ...st.meta, savedTo: null } }));
      if (!r.stillHere) { newProject(); navigate('/my'); }
    }
    toast(r.stillHere ? 'Removed from your email. It’s still saved in this browser.' : 'Removed from your email. It stays saved in the browser it was made in.');
    return true;
  } catch (e) { toast((e as Error).message, { bad: true }); return false; }
}

/** Email me the steps again (only ever to the address it's saved to). */
export async function emailSteps(id: string): Promise<void> {
  try {
    const r = await api.emailSteps(id);
    toast('We’ve emailed the steps to ' + r.sentTo + '.');
  } catch (e) { toast((e as Error).message, { bad: true, ms: 9000 }); }
}

/** Edit a copy (an ordered gangsheet): a new draft with the same artwork, opened straight away. */
export async function editCopy(id: string): Promise<void> {
  set({ busy: { title: 'Making your copy…' } });
  try {
    const r = await api.duplicate(id);
    set({ busy: null });
    navigate('/p/' + r.id);
    toast('This is a new copy. Change it and order it like any gangsheet.');
  } catch (e) {
    set({ busy: null });
    toast((e as Error).message, { bad: true });
  }
}
